import { useState, useEffect } from "react";
import axios from "axios";
import RemoveUser from "./RemoveUser"; // Import RemoveUser component
import { useLanguage } from "../../contexts/LanguageContext";

function UserManagement() {
  const [searchTerm, setSearchTerm] = useState("");
  const [roleFilter, setRoleFilter] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [users, setUsers] = useState([]);
  const [isRemoveModalOpen, setIsRemoveModalOpen] = useState(false); // Add state for modal
  const [selectedUserId, setSelectedUserId] = useState(null); // Add state for selected user
  const { t } = useLanguage();

  useEffect(() => {
    axios.get("http://localhost:5000/users")
      .then((response) => setUsers(response.data))
      .catch((error) => console.error("Error fetching users:", error));
  }, []);

  const handleRemoveUser = (userId) => {
    setSelectedUserId(userId); // Set the selected user ID
    setIsRemoveModalOpen(true); // Open the modal
  };

  const confirmRemoveUser = () => {
    axios.put(`http://localhost:5000/users/${selectedUserId}/block`)
      .then(() => {
        setUsers((prevUsers) =>
          prevUsers.map((user) =>
            user.id === selectedUserId ? { ...user, status: "Blocked" } : user
          )
        );
        setIsRemoveModalOpen(false); // Close the modal
      })
      .catch((error) => console.error("Error blocking user:", error));
  };

  const filteredUsers = users.filter((user) => {
    const matchesSearch =
      searchTerm === "" ||
      user.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesRole = roleFilter === "" || roleFilter === "filter-role" || user.role === roleFilter;
    const matchesStatus = statusFilter === "" || statusFilter === "filter-status" || user.status === statusFilter;

    return matchesSearch && matchesRole && matchesStatus;
  });

  const selectStyle = {
    padding: '10px 14px', background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)',
    borderRadius: '10px', color: 'var(--text-secondary)', fontSize: '0.85rem', outline: 'none', cursor: 'pointer',
  };

  const getRoleColor = (role) => {
    if (role === 'admin') return { bg: 'rgba(99,102,241,0.1)', color: '#818cf8', border: 'rgba(99,102,241,0.2)' };
    if (role === 'professional') return { bg: 'rgba(16,185,129,0.1)', color: '#34d399', border: 'rgba(16,185,129,0.2)' };
    return { bg: 'rgba(245,158,11,0.1)', color: '#fbbf24', border: 'rgba(245,158,11,0.2)' };
  };

  const getStatusColor = (status) => {
    if (status === 'active') return { bg: 'rgba(16,185,129,0.1)', color: '#34d399' };
    if (status === 'suspended') return { bg: 'rgba(245,158,11,0.1)', color: '#fbbf24' };
    return { bg: 'rgba(248,113,113,0.1)', color: '#f87171' };
  };

  return (
    <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '32px 24px' }}>
      <h1 style={{ fontSize: '1.6rem', fontWeight: 700, color: 'var(--text-primary)', fontFamily: 'var(--font-display)', marginBottom: '24px' }}>{t('userManagementDashboard')}</h1>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '24px' }}>
        <input
          type="text"
          placeholder={t('searchUsersPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={{ flex: 1, minWidth: '220px', padding: '10px 14px', background: 'var(--bg-elevated)', border: '1px solid var(--border-subtle)', borderRadius: '10px', color: 'var(--text-primary)', fontSize: '0.85rem', outline: 'none' }}
        />
        <select value={roleFilter} onChange={(e) => setRoleFilter(e.target.value)} style={selectStyle}>
          <option value="filter-role">{t('filterByRole')}</option>
          <option value="professional">{t('professional')}</option>
          <option value="client">{t('business')}</option>
          <option value="admin">{t('admin')}</option>
        </select>
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)} style={selectStyle}>
          <option value="filter-status">{t('filterByStatus')}</option>
          <option value="active">{t('active')}</option>
          <option value="suspended">{t('suspended')}</option>
          <option value="blocked">{t('blocked')}</option>
        </select>
      </div>

      <div style={{ background: 'var(--bg-card)', border: '1px solid var(--border-glass)', borderRadius: '16px', overflow: 'hidden' }}>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr style={{ background: 'var(--bg-elevated)' }}>
                {[t('userId'), t('fullName'), t('emailAddress'), t('role'), t('status'), t('actions')].map((h, i) => (
                  <th key={h} style={{ padding: '14px 20px', textAlign: i === 5 ? 'center' : 'left', fontSize: '0.7rem', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.08em', color: 'var(--text-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filteredUsers.map((user) => {
                const rc = getRoleColor(user.role);
                const sc = getStatusColor(user.status);
                return (
                  <tr key={user.id} style={{ borderBottom: '1px solid var(--border-subtle)', transition: 'background 0.15s' }}
                    onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-glass)'}
                    onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                    <td style={{ padding: '12px 20px', fontSize: '0.8rem', color: 'var(--text-muted)' }}>{user.id}</td>
                    <td style={{ padding: '12px 20px', fontSize: '0.85rem', color: 'var(--text-primary)', fontWeight: 600 }}>{user.name}</td>
                    <td style={{ padding: '12px 20px', fontSize: '0.8rem', color: 'var(--text-secondary)' }}>{user.email}</td>
                    <td style={{ padding: '12px 20px' }}>
                      <span style={{ padding: '3px 10px', borderRadius: '9999px', fontSize: '0.7rem', fontWeight: 600, background: rc.bg, color: rc.color, border: `1px solid ${rc.border}`, textTransform: 'capitalize' }}>{user.role}</span>
                    </td>
                    <td style={{ padding: '12px 20px' }}>
                      <span style={{ padding: '3px 10px', borderRadius: '9999px', fontSize: '0.7rem', fontWeight: 600, background: sc.bg, color: sc.color, textTransform: 'capitalize' }}>{user.status}</span>
                    </td>
                    <td style={{ padding: '12px 20px', textAlign: 'center' }}>
                      <button onClick={() => handleRemoveUser(user.id)} style={{ padding: '6px 14px', borderRadius: '8px', border: '1px solid rgba(248,113,113,0.3)', background: 'rgba(248,113,113,0.1)', color: '#f87171', fontWeight: 600, cursor: 'pointer', fontSize: '0.7rem', transition: 'all 0.2s' }}
                        onMouseEnter={e => { e.target.style.background = '#f87171'; e.target.style.color = '#fff'; }}
                        onMouseLeave={e => { e.target.style.background = 'rgba(248,113,113,0.1)'; e.target.style.color = '#f87171'; }}>
                        {t('removeUser')}
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      <RemoveUser
        isOpen={isRemoveModalOpen}
        closeModal={() => setIsRemoveModalOpen(false)}
        confirmRemove={confirmRemoveUser}
      />
    </div>
  );
}

export default UserManagement;

